import React, { useState } from 'react';
import axios from 'axios';
import { getIn, useFormikContext } from 'formik';
import { isEmpty, omit } from 'lodash';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import { WithConfirmFormik } from '~/components/ConfirmModal';
import DeactivateContactDialog from '~/components/Contacts/DeactivateContactDialog';
import { FINANCE_TAB_KEY } from '~/components/Contacts/UpsertContact/Tabs/Finance/constants';
import type { SpecialValidationRequirementsType } from '~/components/Contacts/UpsertContact/types';
import UpsertContactFormik from '~/components/Contacts/UpsertContact/UpsertContactFormik';
import { useContactTabs } from '~/components/Contacts/UpsertContact/useContactTabs';
import { PERMISSION_ACTIONS, PERMISSION_VERBS, PermissionsButtonWrapper } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import FsIconButton from '~/components/core/FsWrappers/FsIconButton/FsIconButton';
import InnerDialogTabs from '~/components/core/Layout/InnerTabs/InnerDialogTabs';
import { useCms } from '~/components/hooks/useCms';
import TrashIcon from '~/components/icons/TrashIcon';
import useOrganization from '~/components/OrganizationContext';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { reportErrorInProductionOrThrow } from '~/Utils';

import { ContactContextProvider, useContact } from '../useContact';

function assertNoClaimFields(payload: Record<string, unknown>) {
  const found: string[] = [];
  if ('exposure_ids' in payload) found.push('exposure_ids');
  for (const [section, value] of Object.entries(payload)) {
    if (value && typeof value === 'object' && 'claim_document_id' in value) {
      found.push(`${section}.claim_document_id`);
    }
  }
  if (found.length > 0) {
    reportErrorInProductionOrThrow(
      `Contact update without claim context contains claim-specific fields: ${found.join(', ')}`
    );
  }
}

interface EditContactDialogInnerProps {
  onClose: () => void;
  title?: string;
  buttonsOverride?: (prop: {
    isSubmitting: boolean;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  }) => React.ReactElement;
  initialTabKey?: string;
  onDeactivated?: () => void;
}

interface EditContactProps extends EditContactDialogInnerProps {
  contact: ContactFullModel;
  onContactUpdate: () => Promise<void>;
  specialValidationRequirements?: SpecialValidationRequirementsType;
}

const EditContactDialogInner: React.FC<EditContactDialogInnerProps> = ({
  onClose,
  title = 'Edit Contact',
  buttonsOverride,
  initialTabKey,
  onDeactivated,
}) => {
  const { originalContact } = useContact();
  const { isSubmitting, handleSubmit, errors, touched } = useFormikContext();
  const { filteredTabs } = useContactTabs();
  const { userOrganization } = useCms();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);

  const isSystemContact = originalContact?.role
    ? !!organizationContactRolesDict[originalContact.role]?.is_system
    : false;

  const showDeactivateButton = originalContact && !originalContact.is_deactivated && !isSystemContact;

  const initialTabIndex = initialTabKey
    ? Math.max(
        0,
        filteredTabs.findIndex((tab) => tab.tabKey === initialTabKey)
      )
    : 0;

  const tabHasErrors = (tabKey: string) =>
    !isEmpty(getIn(errors, tabKey)) &&
    getIn(touched, tabKey) &&
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Object.keys(errors[tabKey]).some((i) => Object.keys(touched[tabKey]).includes(i));

  const tabs = filteredTabs.map((tab) => ({
    hasErrors: tabHasErrors(tab.tabKey),
    label: tab.label,
    component: tab.tabComponent,
  }));

  const saveButton = (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
      Save
    </Button>
  );

  return (
    <CardDialog
      isDialog
      title={title}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      action={
        showDeactivateButton ? (
          <PermissionsButtonWrapper action={PERMISSION_ACTIONS.DEACTIVATE_CONTACT} verb={PERMISSION_VERBS.WRITE}>
            <FsIconButton
              onClick={() => setIsDeactivateDialogOpen(true)}
              icon={TrashIcon}
              tooltipText="Deactivate contact"
            />
          </PermissionsButtonWrapper>
        ) : undefined
      }
    >
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <InnerDialogTabs tabs={tabs} initialTab={initialTabIndex} />
      <div className="mt-px flex w-full justify-end">
        {buttonsOverride ? (
          buttonsOverride({ isSubmitting, handleSubmit })
        ) : (
          <>
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            {originalContact ? (
              <WithConfirmFormik
                title="Are you sure?"
                contentText={`Are you sure you want to change the contact details for ${originalContact.full_name}? This change will take effect everywhere this contact is referenced, across all claims this contact belongs to`}
                primaryButtonName="Save"
                shouldCloseOnPrimary={true}
              >
                {saveButton}
              </WithConfirmFormik>
            ) : (
              saveButton
            )}
          </>
        )}
      </div>
      {originalContact && isDeactivateDialogOpen && (
        <DeactivateContactDialog
          contact={originalContact}
          organizationId={userOrganization.id}
          onClose={() => setIsDeactivateDialogOpen(false)}
          onDeactivated={() => {
            setIsDeactivateDialogOpen(false);
            onClose();
            onDeactivated?.();
          }}
        />
      )}
    </CardDialog>
  );
};

// The backend applies `trusted_payee` as its own top-level PATCH section, independent of `finance`.
// The finance tab builds it under `finance.trusted_payee` so the permission sanitizer (which runs on
// the tab-keyed payload) can strip it for denied users; hoist it to the top level afterwards.
const hoistTrustedPayeeToTopLevel = (payload: Record<string, unknown>): Record<string, unknown> => {
  const finance = payload[FINANCE_TAB_KEY];
  if (!finance || typeof finance !== 'object') {
    return payload;
  }
  if (!('trusted_payee' in finance)) {
    // A trusted-payee-only edit builds `finance: { trusted_payee }` and nothing else; for a user
    // without TRUSTED_PAYEE_ARRANGEMENT the sanitizer strips that key and leaves an empty section
    // behind, which the backend would reject as a finance update with no change reason.
    return isEmpty(finance) ? omit(payload, FINANCE_TAB_KEY) : payload;
  }
  const { trusted_payee: trustedPayee, ...restFinance } = finance as Record<string, unknown>;
  return {
    ...payload,
    [FINANCE_TAB_KEY]: isEmpty(restFinance) ? undefined : restFinance,
    trusted_payee: trustedPayee,
  };
};

const EditContactFormik: React.FC<EditContactProps> = ({
  contact: existingContact,
  onClose,
  onContactUpdate,
  title,
  buttonsOverride,
  specialValidationRequirements = {},
  initialTabKey,
  onDeactivated,
}) => {
  const { userOrganization } = useCms();
  const { claim } = useClaim();

  const handleSubmit = async (payload: Record<string, unknown>) => {
    if (claim) {
      const url = `/api/v1/organizations/${userOrganization.id}/claim_contacts/claims/${claim.id}/contacts/${existingContact.id}`;
      await axios.patch(url, payload);
    } else {
      assertNoClaimFields(payload);
      const url = `/api/v1/organizations/${userOrganization.id}/contacts/${existingContact.id}/v2`;
      await axios.patch(url, payload);
    }
    await onContactUpdate();
  };

  return (
    <UpsertContactFormik
      contact={existingContact}
      onClose={onClose}
      onSubmit={handleSubmit}
      onPreparePayload={hoistTrustedPayeeToTopLevel}
      specialValidationRequirements={specialValidationRequirements}
      dialogInnerComponent={
        <EditContactDialogInner
          onClose={onClose}
          title={title}
          buttonsOverride={buttonsOverride}
          initialTabKey={initialTabKey}
          onDeactivated={onDeactivated}
        />
      }
    />
  );
};

const EditContactDialog: React.FC<EditContactProps> = ({ contact, ...restProps }) => {
  return (
    <ContactContextProvider contact={contact}>
      <EditContactFormik contact={contact} {...restProps} />
    </ContactContextProvider>
  );
};

export { EditContactDialog, hoistTrustedPayeeToTopLevel };
